import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { vi } from 'date-fns/locale';
import { reportsService, type AdminStats } from '../services/reportsService';
import { loanService } from '../services/loanService';
import { useAppStore } from '../stores/useAppStore';
import { canManageBooks } from '../utils/rbac';
import { useDebounce } from '../hooks/usePerformance';
import { useSnackbar } from '../hooks/useSnackbar';
import { ToastNotification } from '../components/ToastNotification';
import { ExportDialog } from '../components/ExportDialog';
import { type ExportColumn } from '../services/exportService';
import { isStudent } from '../utils/rbac';

export const OverdueBooksPage: React.FC = () => {
  const { user } = useAppStore();
  const userRoles = user?.roles || [];
  const canProcess = canManageBooks(userRoles);
  const canExport = !isStudent(userRoles);

  const [adminStats, setAdminStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLoanIds, setSelectedLoanIds] = useState<string[]>([]);
  const [bulkProcessDialogOpen, setBulkProcessDialogOpen] = useState(false);
  const [exportOverdueDialogOpen, setExportOverdueDialogOpen] = useState(false);
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  // Convert dates to strings for debouncing
  const startDateStr = startDate
    ? `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`
    : '';
  const endDateStr = endDate
    ? `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`
    : '';

  // Debounced values
  const debouncedSearchTerm = useDebounce(searchTerm, 800);
  const debouncedStartDateStr = useDebounce(startDateStr, 800);
  const debouncedEndDateStr = useDebounce(endDateStr, 800);

  const fetchOverdueBooks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const stats = await reportsService.getAdminStats({
        search: debouncedSearchTerm && debouncedSearchTerm.trim() ? debouncedSearchTerm.trim() : undefined,
        startDate: debouncedStartDateStr || undefined,
        endDate: debouncedEndDateStr || undefined
      });

      setAdminStats(stats);
    } catch (err) {
      // Error fetching overdue books
      setError('Không thể tải danh sách sách trả chậm. Vui lòng thử lại sau.');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearchTerm, debouncedStartDateStr, debouncedEndDateStr]);

  useEffect(() => {
    fetchOverdueBooks();
  }, [fetchOverdueBooks]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setStartDate(null);
    setEndDate(null);
  };

  const handleRefresh = () => {
    // Clear all filters and reload
    setSearchTerm('');
    setStartDate(null);
    setEndDate(null);
    fetchOverdueBooks();
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('vi-VN');
    } catch {
      return dateString;
    }
  };

  const handleSendReminder = (bookId: string, borrower: string) => {
    // Mock function for sending reminder
    // In real implementation, this would call an API to send email/SMS
  };

  const handleContactBorrower = (borrower: string) => {
    // Mock function for contacting borrower
    // In real implementation, this would open contact form or call API
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const overdueBooks = adminStats?.overdueBooks || [];
    if (event.target.checked) {
      const allLoanIds = overdueBooks.map((book, index) => book.loanId || book.id || `loan-${index}`);
      setSelectedLoanIds(allLoanIds);
    } else {
      setSelectedLoanIds([]);
    }
  };

  const handleSelectLoan = (loanId: string) => {
    setSelectedLoanIds(prev => {
      if (prev.includes(loanId)) {
        return prev.filter(id => id !== loanId);
      } else {
        return [...prev, loanId];
      }
    });
  };

  const handleBulkProcessClick = () => {
    if (selectedLoanIds.length === 0) return;
    setBulkProcessDialogOpen(true);
  };

  const handleBulkProcessConfirm = async () => {
    try {
      const response = await loanService.returnBooks(selectedLoanIds);

      if (response.success) {
        showSuccess(`Đã xử lý thành công ${response.returnedCount} sách trả chậm`);
        setBulkProcessDialogOpen(false);
        setSelectedLoanIds([]);
        await fetchOverdueBooks();
      } else {
        showError(response.message || 'Không thể xử lý sách trả chậm');
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Không thể xử lý sách trả chậm');
    }
  };

  const handleBulkProcessCancel = () => {
    setBulkProcessDialogOpen(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const overdueBooks = adminStats?.overdueBooks || [];

  // Export handler
  const handleExportOverdue = async (dateFrom: Date | null, dateTo: Date | null): Promise<any[]> => {
    return await loanService.exportOverdueBooks(dateFrom, dateTo);
  };

  // Export column definitions
  const overdueExportColumns: ExportColumn[] = [
    { key: 'book_title', label: 'Tên sách', width: 30 },
    { key: 'book_author', label: 'Tác giả', width: 25 },
    { key: 'book_isbn', label: 'ISBN', width: 20 },
    { key: 'user_fullname', label: 'Người mượn', width: 25 },
    { key: 'user_email', label: 'Email', width: 25 },
    { key: 'user_phone', label: 'Số điện thoại', width: 15 },
    { key: 'duedate', label: 'Ngày hết hạn', width: 15 },
    { key: 'days_overdue', label: 'Số ngày quá hạn', width: 15 }
  ];

  return (
    <>
      <Card>
        <CardContent>
          <Paper sx={{ p: 2, mb: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={vi}>
              <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                <TextField
                  size="small"
                  placeholder="Tìm kiếm tổng hợp (Tên sách, Tên người mượn, số điện thoại, email)..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flexGrow: 1, minWidth: 250 }}
                />
                <DatePicker
                  label="Từ ngày"
                  value={startDate}
                  onChange={(newDate) => setStartDate(newDate)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { minWidth: 150 }
                    }
                  }}
                />
                <DatePicker
                  label="Đến ngày"
                  value={endDate}
                  onChange={(newDate) => setEndDate(newDate)}
                  minDate={startDate || undefined}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { minWidth: 150 }
                    }
                  }}
                />
                <Tooltip title="Làm mới" arrow>
                  <IconButton
                    color="primary"
                    onClick={handleRefresh}
                    aria-label="Làm mới"
                    size="small"
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {canExport && (
                  <Tooltip title="Xuất Excel" arrow>
                    <IconButton
                      color="primary"
                      onClick={() => setExportOverdueDialogOpen(true)}
                      aria-label="Xuất Excel"
                      size="small"
                    >
                      <FileDownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {canProcess && selectedLoanIds.length > 0 && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<CheckCircleIcon />}
                    onClick={handleBulkProcessClick}
                  >
                    Xử lý ({selectedLoanIds.length})
                  </Button>
                )}
              </Box>
            </LocalizationProvider>
          </Paper>

          {overdueBooks.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="text.secondary">
                Không có sách nào trả chậm
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ minHeight: 200, width: '100%' }}>
              <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '33%' }}>Tên Sách</TableCell>
                    <TableCell sx={{ width: '14%' }}>Ngày Hết Hạn</TableCell>
                    <TableCell sx={{ width: '20%' }}>Trạng Thái</TableCell>
                    <TableCell sx={{ width: '20%' }}>Hành Động</TableCell>
                    {canProcess && (
                      <TableCell padding="none" align="center" sx={{ width: '14px', minWidth: '14px', maxWidth: '14px', padding: '0px !important' }}>
                        <Checkbox
                          indeterminate={selectedLoanIds.length > 0 && selectedLoanIds.length < overdueBooks.length}
                          checked={overdueBooks.length > 0 && selectedLoanIds.length === overdueBooks.length}
                          onChange={handleSelectAll}
                          size="small"
                          sx={{ padding: '0px', '& svg': { fontSize: '20px' } }}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {overdueBooks.map((book, index) => {
                    const loanId = book.loanId || book.id || `loan-${index}`;
                    const isSelected = selectedLoanIds.includes(loanId);

                    return (
                      <TableRow key={loanId} hover>
                        <TableCell>
                          {book.title.length > 40 ? (
                            <Tooltip title={book.title} arrow>
                              <Typography
                                variant="body2"
                                fontWeight="medium"
                                sx={{
                                  maxWidth: '100%',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  display: 'block',
                                  cursor: 'default'
                                }}
                              >
                                {book.title.substring(0, 40)}...
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" fontWeight="medium">
                              {book.title}
                            </Typography>
                          )}
                          {book.borrower && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                              👤 {book.borrower}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{formatDate(book.dueDate)}</TableCell>
                        <TableCell>
                          <Chip
                            label={`Trả chậm ${book.daysOverdue} ngày`}
                            color={book.daysOverdue > 10 ? 'error' : book.daysOverdue > 5 ? 'warning' : 'default'}
                            size="small"
                            icon={<WarningIcon />}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Gửi nhắc nhở">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleSendReminder(book.id, book.borrower)}
                              >
                                <EmailIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Liên hệ">
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => handleContactBorrower(book.borrower)}
                              >
                                <PhoneIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        {canProcess && (
                          <TableCell padding="none" align="center" sx={{ width: '14px', minWidth: '14px', maxWidth: '14px', padding: '0px !important' }}>
                            <Checkbox
                              checked={isSelected}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleSelectLoan(loanId);
                              }}
                              size="small"
                              sx={{ padding: '0px', '& svg': { fontSize: '20px' } }}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Bulk Process Confirmation Dialog */}
      <Dialog open={bulkProcessDialogOpen} onClose={handleBulkProcessCancel}>
        <DialogTitle>Xác nhận xử lý sách trả chậm</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Bạn có chắc chắn muốn xử lý {selectedLoanIds.length} sách trả chậm không?
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Hành động này sẽ đánh dấu các sách đã được trả và cập nhật số lượng sách có sẵn.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkProcessCancel}>Hủy</Button>
          <Button onClick={handleBulkProcessConfirm} color="primary" variant="contained">
            Xử lý
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      <ExportDialog
        open={exportOverdueDialogOpen}
        onClose={() => setExportOverdueDialogOpen(false)}
        onExport={handleExportOverdue}
        columns={overdueExportColumns}
        fileName="Danh_sach_sach_qua_han"
        sheetName="Sách quá hạn"
        title="Xuất danh sách sách quá hạn ra Excel"
        description="Chọn khoảng thời gian để xuất dữ liệu sách quá hạn. Dữ liệu sẽ được xuất theo ngày hết hạn."
        requireDateRange={true}
      />

      <ToastNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
      />
    </>
  );
};

export default OverdueBooksPage;
